import { transformEnumToDisplay } from '@fruitful/member-profile';
import {
    type GuidanceSession,
    type GuidanceSessionAutocancelReason,
    GuidanceSessionStatus,
} from '@fruitful/types';
import { parseISO } from 'date-fns';
import React from 'react';
import { CheckIcon, Icon, TooltipTrigger, XStack } from 'smoothie';

import { formatToSimpleDateTimeString } from '../../common/utils/formatting';

export const GuidanceSessionStatusCell = ({
    status,
    startTime,
    endTime,
    autoCancelReason,
    confirmedAt,
}: {
    status: GuidanceSessionStatus;
    startTime: string;
    endTime: string;
    autoCancelReason: GuidanceSessionAutocancelReason | null | undefined;
    confirmedAt: string | null | undefined;
}): React.ReactNode => {
    let label: string;

    switch (status) {
        case GuidanceSessionStatus.SCHEDULED: {
            const now = new Date();
            // DB dates are in UTC
            const start = parseISO(startTime);
            const end = parseISO(endTime);

            if (now < start) {
                label = 'Scheduled';
            } else {
                label = now < end ? 'In Progress' : 'Completed';
            }
            break;
        }
        case GuidanceSessionStatus.CANCELED:
            label =
                autoCancelReason != null
                    ? `Auto-canceled (${transformEnumToDisplay(autoCancelReason)})`
                    : 'Canceled';
            break;
        case GuidanceSessionStatus.COMPLETED:
        case GuidanceSessionStatus.CONFIRMED:
        case GuidanceSessionStatus.NO_SHOWED:
            label = transformEnumToDisplay(status);
            break;
        default: {
            const _exhaustive: never = status;
            return _exhaustive;
        }
    }

    return (
        <XStack gap={6} alignItems="center">
            {label}
            {confirmedAt != null && (
                <TooltipTrigger
                    tooltipContent={`Confirmed via SMS on ${formatToSimpleDateTimeString(
                        confirmedAt,
                    )}`}
                >
                    <Icon icon={CheckIcon} size={16} color="$green1100" />
                </TooltipTrigger>
            )}
        </XStack>
    );
};

export const guidanceSessionStatusFromSession = (
    session: GuidanceSession,
): React.ReactNode => (
    <GuidanceSessionStatusCell
        status={session.status}
        startTime={session.startTime}
        endTime={session.endTime}
        autoCancelReason={session.autoCancelReason}
        confirmedAt={session.confirmedAt}
    />
);
